import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Routes, Route, Navigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  GraduationCap,
  Users,
  FolderOpen,
  Settings,
  Trash2,
  ClipboardList,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api, { adminReq } from "../lib/api";
import { toast } from "sonner";
import AdminOverview from "./admin/Overview";
import AdminApplications from "./admin/Applications";
import AdminDomestic from "./admin/Domestic";
import AdminRecruiters from "./admin/Recruiters";
import AdminDocuments from "./admin/Documents";
import AdminSettings from "./admin/Settings";
import AdminTrash from "./admin/Trash";
import AdminStudentAid from "./admin/StudentAid";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [data, setData] = useState({
    stats: null,
    apps: [],
    domestic: [],
    recruiters: [],
    loading: true,
  });

  const admin =
    JSON.parse(localStorage.getItem("cct_admin") || "{}") || { name: "Admin" };

  const loadAll = async () => {
    setData((p) => ({ ...p, loading: true }));
    try {
      const [s, a, d, r] = await Promise.all([
        api.get("/admin/stats", adminReq()),
        api.get("/admin/applications", adminReq()),
        api.get("/admin/applications/domestic", adminReq()),
        api.get("/admin/recruiters", adminReq()),
      ]);
      setData({
        stats: s.data,
        apps: a.data,
        domestic: d.data,
        recruiters: r.data,
        loading: false,
      });
    } catch {
      toast.error("Session expired. Please log in again.");
      logout();
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem("cct_admin_token");
    localStorage.removeItem("cct_admin");
    navigate("/admin-login");
  };

  const updateRecruiterStatus = async (id, status) => {
    try {
      await api.patch(
        `/admin/recruiters/${id}/status`,
        { status },
        adminReq()
      );
      toast.success(`Recruiter ${status}`);
      loadAll();
    } catch {
      toast.error("Failed to update");
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await api.get(`/admin/files/${fileId}`, {
        ...adminReq(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const sidebarItems = useMemo(
    () => [
      {
        to: "/admin/dashboard",
        end: true,
        icon: LayoutDashboard,
        label: "Overview",
      },
      {
        to: "/admin/dashboard/applications",
        icon: FileText,
        label: "Applications",
        badge: data.apps.length,
      },
      {
        to: "/admin/dashboard/domestic",
        icon: GraduationCap,
        label: "Domestic",
        badge: data.domestic.length,
      },
      {
        to: "/admin/dashboard/recruiters",
        icon: Users,
        label: "Recruiters",
        badge: data.stats?.recruiters_pending || 0,
      },
      {
        to: "/admin/dashboard/student-aid",
        icon: ClipboardList,
        label: "Student Aid",
      },
      {
        to: "/admin/dashboard/documents",
        icon: FolderOpen,
        label: "Documents",
      },
      {
        to: "/admin/dashboard/trash",
        icon: Trash2,
        label: "Trash",
      },
      {
        to: "/admin/dashboard/settings",
        icon: Settings,
        label: "Settings",
      },
    ],
    [data]
  );

  const titleMap = {
    "/admin/dashboard": ["Overview", "Real-time admissions analytics"],
    "/admin/dashboard/applications": [
      "Student Applications",
      "All international student applications",
    ],
    "/admin/dashboard/domestic": [
      "Domestic Applications",
      "Detailed Canadian student applications",
    ],
    "/admin/dashboard/recruiters": [
      "Recruiters",
      "Review and approve partner applications",
    ],
    "/admin/dashboard/student-aid": [
      "Student Aid Master Record",
      "Track document status, LOA/CLOA, COE and SEC forms",
    ],
    "/admin/dashboard/documents": [
      "Documents",
      "All uploaded files across applications",
    ],
    "/admin/dashboard/trash": [
      "Trash",
      "Soft-deleted items \u2014 restore or permanently delete",
    ],
    "/admin/dashboard/settings": ["Settings", "Account and system settings"],
  };
  const [pageTitle, pageSubtitle] = titleMap[location.pathname] || [
    "Overview",
    "",
  ];

  return (
    <div className="min-h-screen bg-cct-ambient">
      <div className="flex">
        <Sidebar
          items={sidebarItems}
          user={admin}
          roleLabel="Admin"
          onLogout={logout}
          collapsed={collapsed}
        />
        <div className="flex-1 min-w-0">
          <Topbar
            title={pageTitle}
            subtitle={pageSubtitle}
            onToggleSidebar={() => setCollapsed((c) => !c)}
            rightSlot={
              <button
                onClick={loadAll}
                className="hidden md:inline-flex items-center text-xs font-medium text-cct-blue hover:underline"
              >
                Refresh
              </button>
            }
          />

          <main className="p-5 sm:p-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Routes>
                  <Route
                    index
                    element={
                      <AdminOverview
                        data={data}
                        adminName={admin.name}
                        onApprove={(id) =>
                          updateRecruiterStatus(id, "approved")
                        }
                      />
                    }
                  />
                  <Route
                    path="applications"
                    element={
                      <AdminApplications
                        rows={data.apps}
                        onDownload={downloadFile}
                        onRefresh={loadAll}
                      />
                    }
                  />
                  <Route
                    path="domestic"
                    element={
                      <AdminDomestic
                        rows={data.domestic}
                        onRefresh={loadAll}
                      />
                    }
                  />
                  <Route
                    path="recruiters"
                    element={
                      <AdminRecruiters
                        rows={data.recruiters}
                        onStatus={updateRecruiterStatus}
                        onDownload={downloadFile}
                        onRefresh={loadAll}
                      />
                    }
                  />
                  <Route
                    path="documents"
                    element={
                      <AdminDocuments
                        apps={data.apps}
                        recruiters={data.recruiters}
                        onDownload={downloadFile}
                      />
                    }
                  />
                  <Route path="student-aid" element={<AdminStudentAid />} />
                  <Route path="trash" element={<AdminTrash />} />
                  <Route
                    path="settings"
                    element={<AdminSettings admin={admin} />}
                  />
                  <Route
                    path="*"
                    element={<Navigate to="/admin/dashboard" replace />}
                  />
                </Routes>
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
}
